/**
 * The drill's queue (日本-601) — pure, so every rule below is tested without a DOM.
 *
 * A drill is **its own run and writes nothing anywhere**: this state lives in the tab, is thrown
 * away on leaving, and a reload starts over. The rules are Alex's:
 *
 * - the order is **shuffled**, and each card appears **once** however many chosen groups hold it;
 * - a **missed card comes back** a few cards later, and keeps coming back until it is passed;
 * - the run ends when every card has been passed once.
 *
 * `↑` undoes the last grade, and only here — nothing was sent to the scheduler, so there is
 * nothing to take back anywhere else.
 */

export type Rng = () => number;

export interface DrillState {
  /** Card ids still to be shown, head first. */
  queue: number[];
  /** Card ids passed, in the order they were passed. */
  passed: number[];
  /** Card id → how many times it was missed. A card never missed is absent. */
  misses: Record<number, number>;
  /** Every card id in the run, in its first shuffled order. Fixed for the life of the run. */
  all: number[];
  /** The states before each grade, newest last — what `↑` walks back through. */
  history: Omit<DrillState, "history">[];
}

/** How far back a missed card goes: between these many cards later, inclusive. Close enough that
 *  the confusion is still fresh, far enough that it is not simply the answer you just saw. */
export const REINSERT_MIN = 3;
export const REINSERT_MAX = 5;

export function shuffle<T>(items: readonly T[], rng: Rng = Math.random): T[] {
  const out = [...items];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    const held = out[i] as T;
    out[i] = out[j] as T;
    out[j] = held;
  }
  return out;
}

export function startDrill(cardIds: readonly number[], rng: Rng = Math.random): DrillState {
  const unique = [...new Set(cardIds)];
  const order = shuffle(unique, rng);
  return { queue: order, passed: [], misses: {}, all: order, history: [] };
}

export function currentCard(state: DrillState): number | null {
  return state.queue[0] ?? null;
}

export function isFinished(state: DrillState): boolean {
  return state.queue.length === 0;
}

function snapshot(state: DrillState): Omit<DrillState, "history"> {
  return { queue: state.queue, passed: state.passed, misses: state.misses, all: state.all };
}

export function grade(state: DrillState, passed: boolean, rng: Rng = Math.random): DrillState {
  const head = currentCard(state);
  if (head === null) return state;
  const rest = state.queue.slice(1);
  const history = [...state.history, snapshot(state)];
  if (passed) {
    return { ...state, queue: rest, passed: [...state.passed, head], history };
  }
  const span = REINSERT_MAX - REINSERT_MIN + 1;
  const offset = REINSERT_MIN + Math.min(span - 1, Math.floor(rng() * span));
  const at = Math.min(rest.length, offset);
  const queue = [...rest.slice(0, at), head, ...rest.slice(at)];
  const misses = { ...state.misses, [head]: (state.misses[head] ?? 0) + 1 };
  return { ...state, queue, misses, history };
}

export function canUndo(state: DrillState): boolean {
  return state.history.length > 0;
}

export function undo(state: DrillState): DrillState {
  if (state.history.length === 0) return state;
  const previous = state.history[state.history.length - 1];
  if (!previous) return state;
  return { ...previous, history: state.history.slice(0, -1) };
}

/** Misses still waiting to come back — the red `再出 N` in the chrome. */
export function pendingRepeats(state: DrillState): number {
  return new Set(state.queue.filter((id) => (state.misses[id] ?? 0) > 0)).size;
}

/** Which pass of this card is on screen: 1 the first time, 2 after one miss… (`2回目`). */
export function attemptOf(state: DrillState, cardId: number): number {
  return (state.misses[cardId] ?? 0) + 1;
}

/** Card ids missed at least once, most-missed first, ties in run order — `7e`'s list, and the
 *  input to `不可だったN枚だけ`. */
export function missedCards(state: DrillState): number[] {
  return state.all
    .filter((id) => (state.misses[id] ?? 0) > 0)
    .sort((a, b) => (state.misses[b] ?? 0) - (state.misses[a] ?? 0));
}

export function totalMisses(state: DrillState): number {
  return Object.values(state.misses).reduce((sum, n) => sum + n, 0);
}

/** Passed on the first showing — the finished screen's 一発合格. */
export function firstTimePasses(state: DrillState): number {
  return state.all.filter((id) => !(state.misses[id] ?? 0) && state.passed.includes(id)).length;
}
